import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { useI18n } from "../contexts/I18nContext";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { Plus, Users, ChevronLeft } from "lucide-react";
import { toast } from "sonner";
import RowActions from "../components/RowActions";
import { useAuth } from "../contexts/AuthContext";

const RESOLVE_PHOTO = (url) =>
  url && url.startsWith("/api/") ? `${process.env.REACT_APP_BACKEND_URL}${url}` : url;

const EMPTY = { full_name: "", specialization: "", phone: "", email: "" };

export default function TeachersPage() {
  const { t } = useI18n();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isSuper = user?.role === "super_admin";
  const [schools, setSchools] = useState([]);
  const [items, setItems] = useState([]);
  const [school, setSchool] = useState("");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);

  useEffect(() => {
    api.get("/schools").then(r => {
      const list = r.data || [];
      setSchools(list);
      setSchool(prev => {
        if (prev) return prev;
        if (!isSuper && user?.school_id && list.some(s => s.id === user.school_id)) return user.school_id;
        return list[0]?.id || "";
      });
    });
  }, [user?.school_id, isSuper]);
  const load = () => { if (school) api.get(`/teachers?school_id=${school}`).then(r => setItems(r.data || [])); };
  useEffect(() => { load(); }, [school]); // eslint-disable-line react-hooks/exhaustive-deps

  const openEdit = (it) => { setEditing(it); setForm({ ...EMPTY, ...it }); setOpen(true); };
  const submit = async () => {
    const payload = { ...form, school_id: school };
    if (editing) await api.put(`/teachers/${editing.id}`, payload);
    toast.success(t("success")); setOpen(false); load();
  };
  const remove = async (it) => { await api.delete(`/teachers/${it.id}`); toast.success(t("success")); load(); };

  return (
    <div className="space-y-6">
      <div className="flex justify-between flex-wrap gap-3 items-center">
        <h1 className="text-3xl font-semibold">{t("teachers")}</h1>
        <div className="flex gap-3 flex-wrap items-center">
          <Select value={school} onValueChange={setSchool} disabled={!isSuper}>
            <SelectTrigger className="w-56" data-testid="teachers-school-select"><SelectValue placeholder={t("select_school")} /></SelectTrigger>
            <SelectContent>{schools.map(s => <SelectItem key={s.id} value={s.id}>{s.name_ar || s.name}</SelectItem>)}</SelectContent>
          </Select>
          <div className="text-xs text-muted-foreground bg-muted/40 px-3 py-2 rounded-lg" data-testid="teachers-add-hint">
            💡 {t("teachers_add_via_hr_hint") || "لإضافة معلم جديد استخدم تبويب الموظفين في الموارد البشرية"}
          </div>
          {/* Edit dialog (opened from a teacher card) — creation is done only via HR/Employees */}
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogContent><DialogHeader><DialogTitle>{t("edit")}</DialogTitle></DialogHeader>
              <div className="space-y-3">
                <div><Label>{t("name")} *</Label><Input value={form.full_name} onChange={e => setForm({ ...form, full_name: e.target.value })} data-testid="t-name" /></div>
                <div><Label>{t("specialization")}</Label><Input value={form.specialization || ""} onChange={e => setForm({ ...form, specialization: e.target.value })} /></div>
                <div><Label>{t("email")}</Label><Input type="email" value={form.email || ""} onChange={e => setForm({ ...form, email: e.target.value })} placeholder="teacher@school.sa" dir="ltr" data-testid="t-email" /></div>
                <div><Label>{t("phone")}</Label><Input value={form.phone || ""} onChange={e => setForm({ ...form, phone: e.target.value })} dir="ltr" /></div>
                <Button onClick={submit} className="w-full" data-testid="teacher-save">{t("save")}</Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
        {items.map(tt => (
          <Card
            key={tt.id}
            className="p-5 border shadow-none card-hover smooth cursor-pointer hover:border-primary/40 transition"
            data-testid={`teacher-${tt.id}`}
            onClick={() => navigate(`/teachers/${tt.id}`)}
          >
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-xl bg-primary/10 text-primary grid place-items-center font-bold shrink-0 overflow-hidden">
                {tt.photo_url ? (
                  <img src={RESOLVE_PHOTO(tt.photo_url)} alt="" className="w-full h-full object-cover" />
                ) : (
                  tt.full_name?.split(" ").slice(0, 2).map((p) => p[0]).join("") || <Users className="w-5 h-5" />
                )}
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-semibold truncate">{tt.full_name}</div>
                <div className="text-xs text-muted-foreground truncate">{tt.specialization || "—"}</div>
                <div className="text-xs text-muted-foreground font-mono truncate" dir="ltr">{tt.phone || tt.email || "—"}</div>
              </div>
              <div onClick={(e) => e.stopPropagation()} className="flex items-center">
                <RowActions item={tt} onEdit={openEdit} onDelete={remove} testIdPrefix="teacher" />
                <ChevronLeft className="w-4 h-4 text-muted-foreground rtl:rotate-180" />
              </div>
            </div>
          </Card>
        ))}
        {items.length === 0 && <Card className="p-8 col-span-full text-center text-sm text-muted-foreground border-dashed">{t("no_data")}</Card>}
      </div>
    </div>
  );
}
